import React, { useState, useRef, useEffect } from 'react';
import { Camera, RefreshCw, Check, X, AlertCircle, Loader2, FlipHorizontal } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { documentService } from '@/services/documentService';
import { DocumentRecord, DocumentType, EntityType } from '@/types/database';

interface PhotoCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: EntityType;
  entityId: string;
  defaultDocumentType?: DocumentType;
  onPhotoCaptured?: (doc: DocumentRecord) => void;
}

export const PhotoCaptureModal: React.FC<PhotoCaptureModalProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  defaultDocumentType = 'weighing_sheet',
  onPhotoCaptured,
}) => {
  const [photoBlob, setPhotoBlob] = useState<Blob | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [docType, setDocType] = useState<DocumentType>(defaultDocumentType);
  const [notes, setNotes] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isLiveStreamActive, setIsLiveStreamActive] = useState<boolean>(false);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nativeCameraInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Stop video stream on unmount/close
  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsLiveStreamActive(false);
  };

  useEffect(() => {
    if (!isOpen) {
      stopStream();
      setPhotoBlob(null);
      setPhotoPreview(null);
      setError(null);
    }
  }, [isOpen]);

  // Start live WebRTC camera stream (if user opts for in-browser viewfinder)
  const startLiveCamera = async () => {
    setError(null);
    try {
      stopStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setIsLiveStreamActive(true);
    } catch (err: any) {
      console.warn('[PhotoCaptureModal] WebRTC camera error, falling back to native capture input:', err);
      setIsLiveStreamActive(false);
      // Automatically trigger hardware capture input
      nativeCameraInputRef.current?.click();
    }
  };

  // Capture frame from live video element
  const captureFrame = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (blob) {
          setPhotoBlob(blob);
          setPhotoPreview(URL.createObjectURL(blob));
          stopStream();
        }
      },
      'image/jpeg',
      0.85
    );
  };

  // Handle image selected via mobile native hardware camera input
  const handleNativeCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoBlob(file);
      setPhotoPreview(URL.createObjectURL(file));
      stopStream();
    }
  };

  const handleRetake = () => {
    setPhotoBlob(null);
    setPhotoPreview(null);
    setError(null);
  };

  const handleUploadPhoto = async () => {
    if (!photoBlob) {
      setError('Please capture a photo first.');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const fileName = `camera_photo_${Date.now()}.jpg`;
      const doc = await documentService.uploadDocument({
        file: photoBlob,
        fileName,
        entityType,
        entityId,
        documentType: docType,
        notes: notes.trim() || undefined,
        folderCategory: documentService.getFolderCategory(docType) as any,
      });

      if (onPhotoCaptured) onPhotoCaptured(doc);
      onClose();
    } catch (err: any) {
      console.error('[PhotoCaptureModal] Photo upload error:', err);
      setError(err.message || 'Failed to upload photo.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md p-4 sm:p-6">
        <DialogHeader>
          <div className="flex items-center gap-2 text-emerald-800">
            <Camera className="w-5 h-5" />
            <DialogTitle>Mobile Camera Photo Capture</DialogTitle>
          </div>
        </DialogHeader>

        {/* Hidden hardware camera input using capture="environment" for rear barn camera */}
        <input
          ref={nativeCameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleNativeCameraCapture}
          className="hidden"
        />
        <canvas ref={canvasRef} className="hidden" />

        <div className="space-y-4 my-2">
          {/* Viewfinder or Captured Preview */}
          <div className="relative aspect-4/3 w-full bg-slate-900 rounded-lg overflow-hidden flex items-center justify-center border border-slate-800">
            {photoPreview ? (
              <img src={photoPreview} alt="Captured preview" className="w-full h-full object-contain" />
            ) : isLiveStreamActive ? (
              <>
                <video ref={videoRef} className="w-full h-full object-cover" playsInline autoPlay muted />
                <div className="absolute top-2 right-2 flex gap-1.5">
                  <button
                    onClick={() => {
                      setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
                      startLiveCamera();
                    }}
                    className="p-2 rounded-full bg-slate-900/70 text-white hover:bg-slate-900"
                    title="Switch camera"
                  >
                    <FlipHorizontal className="w-4 h-4" />
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center p-6 text-center text-slate-300 gap-3">
                <Camera className="w-12 h-12 text-slate-400" />
                <p className="text-sm font-medium">Ready to capture photo</p>
                <div className="flex flex-col gap-2 w-full max-w-xs">
                  <Button
                    onClick={() => nativeCameraInputRef.current?.click()}
                    variant="default"
                    className="w-full bg-emerald-700 hover:bg-emerald-600 flex items-center justify-center gap-2"
                  >
                    <Camera className="w-4 h-4" /> Open Device Camera
                  </Button>
                  <Button
                    onClick={startLiveCamera}
                    variant="outline"
                    className="w-full text-xs text-slate-800 bg-white"
                  >
                    Open Live Viewfinder
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Action buttons while previewing / streaming */}
          {isLiveStreamActive && !photoPreview && (
            <div className="flex justify-center gap-3">
              <Button onClick={captureFrame} variant="default" className="bg-emerald-700 text-white px-6">
                <Camera className="w-4 h-4 mr-2" /> Take Photo
              </Button>
              <Button onClick={stopStream} variant="secondary">
                Cancel Viewfinder
              </Button>
            </div>
          )}

          {/* Captured photo details & confirmation */}
          {photoPreview && (
            <div className="space-y-3 bg-slate-50 p-3.5 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-emerald-800 flex items-center gap-1">
                  <Check className="w-4 h-4" /> Photo Captured
                </span>
                <Button size="sm" variant="ghost" onClick={handleRetake} className="text-xs text-slate-600 h-7">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Retake
                </Button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Document Type</label>
                <select
                  value={docType}
                  onChange={(e) => setDocType(e.target.value as DocumentType)}
                  className="w-full text-sm rounded-md border border-slate-300 bg-white p-2 focus:outline-none focus:ring-2 focus:ring-emerald-600"
                >
                  <option value="weighing_sheet">WEIGHING SHEET PHOTO</option>
                  <option value="invoice">DELIVERY INVOICE / DR</option>
                  <option value="photo">MORTALITY / ANIMAL PHOTO</option>
                  <option value="lab_result">DIAGNOSTIC / ASF CERTIFICATE</option>
                  <option value="general">GENERAL ATTACHMENT</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Optional Notes</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Weighing scale ticket photo"
                  className="w-full text-sm rounded-md border border-slate-300 bg-white p-2 focus:outline-none focus:ring-2 focus:ring-emerald-600"
                />
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 p-3 rounded-md border border-red-200">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={isUploading}>
            Cancel
          </Button>
          {photoPreview && (
            <Button
              variant="default"
              onClick={handleUploadPhoto}
              disabled={isUploading}
              className="bg-emerald-700 hover:bg-emerald-600"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Attaching...
                </>
              ) : (
                'Attach Photo to Record'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
